'use client';

import { Fragment, useRef } from 'react';
import { faBars } from '@fortawesome/pro-regular-svg-icons';

import {
  Box,
  List,
  Button,
  Drawer,
  Heading,
  ListItem,
  DrawerBody,
  DrawerHeader,
} from '@preventx/oxygen';

// Types
import { ISOLanguageKeys } from '@/types/language';
import { SiteNavigationMenuItems } from '@/types/navigation/site';

// Services
import { onNavigate } from '@/services/site/navigation/client';

// Components
import { Link } from '@/components/Actions/Link';
import { DropdownPanel } from '@/components/Site/Navigation/Drawer/DropdownPanel';

const ButtonIdentifier = 'site-header-drawer-navigation-button';
const PopoverDrawerTargetIdentifier = 'site-header-drawer-navigation-container';

type DrawerNavigationProps = {
  openMenuLabel: string;
  navigationTitle: string;
  language: string | ISOLanguageKeys;
  navigation: SiteNavigationMenuItems[];
};

export const DrawerNavigation = ({
  language,
  navigation,
  openMenuLabel,
  navigationTitle,
}: DrawerNavigationProps) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const onClick = () => {
    ref.current?.hidePopover();

    onNavigate();
  };

  return (
    <>
      <Box component="div" className="md:hidden">
        <Button
          type="button"
          variant="icon"
          onClick={() => []}
          id={ButtonIdentifier}
          tooltip={openMenuLabel}
          popoverTarget={PopoverDrawerTargetIdentifier}
          icon={{ definition: faBars, position: 'icon-only' }}
        >
          {openMenuLabel}
        </Button>
      </Box>

      <Drawer
        ref={ref}
        ariaLabelledBy=""
        position="inline-start"
        id={PopoverDrawerTargetIdentifier}
      >
        <DrawerHeader
          closeLabel="Close"
          drawerId={PopoverDrawerTargetIdentifier}
        >
          {navigationTitle}
        </DrawerHeader>

        <DrawerBody>
          <Box component="div" width="w-100%">
            {navigation.map(({ id, link, dropdown, panel }) => (
              <Fragment key={`site-drawer-navigation-${id}`}>
                {dropdown && (
                  <Box component="div" addFlow>
                    <Heading component="h3" className="stack-nav__heading">
                      {dropdown.label}
                    </Heading>

                    <List component="ul" style="stack-nav">
                      {dropdown.items.map(({ id, link }) => (
                        <ListItem key={`drawer-site-navigation-listitem-${id}`}>
                          <Link
                            href={link}
                            type="link"
                            as="default"
                            onClick={onClick}
                            retainSearchParams
                          >
                            {link.text}
                          </Link>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {link && (
                  <List component="ul" style="stack-nav">
                    <ListItem>
                      <Link
                        href={link}
                        type="link"
                        as="default"
                        onClick={onClick}
                        retainSearchParams
                      >
                        {link.text}
                      </Link>
                    </ListItem>
                  </List>
                )}

                {panel && (
                  <DropdownPanel
                    id={id}
                    ref={ref}
                    language={language}
                    onNavigate={onClick}
                    configuration={panel}
                  />
                )}
              </Fragment>
            ))}
          </Box>
        </DrawerBody>
      </Drawer>
    </>
  );
};
